import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import type { Badge } from '../services/conceptPlus1Service';

export function useBadgeCheck(studentId: string | undefined) {
  const [pendingBadges, setPendingBadges] = useState<Badge[]>([]);
  const earnedIds = useRef<Set<number>>(new Set());
  const badgeMap = useRef<Map<string, Badge>>(new Map()); // criteria → Badge
  const loaded = useRef(false);

  useEffect(() => {
    if (!studentId || loaded.current) return;
    loaded.current = true;

    async function loadBadgeState() {
      const [earnedResult, catalogResult] = await Promise.all([
        supabase.from('student_badges').select('badge_id').eq('student_id', studentId),
        supabase.from('badges').select('*').in('badge_type', ['achievement', 'milestone', 'consistency']),
      ]);

      if (earnedResult.data) {
        earnedIds.current = new Set(earnedResult.data.map((r: { badge_id: number }) => r.badge_id));
      }

      if (catalogResult.data) {
        const map = new Map<string, Badge>();
        for (const b of catalogResult.data) {
          if (b.criteria) {
            map.set(b.criteria, {
              badge_id: b.id, id: b.id,
              badge_name: b.name, name: b.name,
              badge_description: b.description, description: b.description,
              badge_type: b.badge_type,
              icon_url: b.icon_url,
              criteria: b.criteria,
              earned_at: null, is_earned: false,
            } as Badge);
          }
        }
        badgeMap.current = map;
      }
    }

    loadBadgeState();
  }, [studentId]);

  const awardByCriteria = useCallback((criteria: string) => {
    if (!studentId) return;
    const badge = badgeMap.current.get(criteria);
    if (!badge) return;
    const badgeId = badge.id ?? badge.badge_id;
    if (badgeId === undefined) return;
    if (earnedIds.current.has(badgeId)) return;

    earnedIds.current.add(badgeId);
    setPendingBadges(prev => [...prev, badge]);

    supabase
      .from('student_badges')
      .insert({ student_id: studentId, badge_id: badgeId })
      .then(() => {});
  }, [studentId]);

  const dismissFirst = useCallback(() => {
    setPendingBadges(prev => prev.slice(1));
  }, []);

  return { awardByCriteria, pendingBadges, dismissFirst };
}
